import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Header } from '@/components/header';
import { DynamicProgressSteps } from '@/components/ui/dynamicProgressSteps';
import { StepActions } from '@/components/patientApp/stepActions';
import { Button } from '@/components/ui/button';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getProcedureQueingBlocksQueryOptions } from '@/api/queries/cueing';
import {
  CueingBlockStatus,
  CueingStepTypeEnum,
  CueingStepStatus,
  LocalOnlyStepStatus,
} from '@/api/schema/cueing';
import { useProcedureManager } from '@/hooks/useProcedureManager';
import { useProcedureSteps } from '@/hooks/useProcedureSteps';
import { logger, openLinkInNewTab } from '@/lib/utils';
import { NextStep } from '@/components/dashboard/nextStep';
import { DashboardCharts } from '@/components/dashboard/dashboardCharts';
import {
  ProcedureScreen,
  ProcedureState,
  ProcedureStepResponse,
  ProcedureStepType,
} from '@/types/procedure';
import {
  useUpdateCravingResultsMutation,
  useUpdateEmotionResultsMutation,
} from '@/api/mutations/craving';
import { PatientConnectionStatus } from '@/components/dashboard/patientConnectionStatus';
import { RepeatVasModal } from '@/components/dashboard/repeatVasModal';
import {
  CommentModal,
  CommentModalProps,
} from '@/components/dashboard/commentModal';
import {
  useAddComment,
  useStopProcedureMutation,
  usePauseProcedureMutation,
  usePauseResumeProcedureMutation,
  useStopResumeProcedureMutation,
  useNextBlockMutation,
  useFinishCueingProcedure,
} from '@/api/mutations/procedures';
import {
  CravingResultsCounter,
  EmotionResultsCounter,
} from '@/lib/resultsCounter';
import { FinishProcedureDialog } from '@/components/dashboard/finishProcedureDialog';
import { useUpdateCueingBlockMutation } from '@/api/mutations/cueing';
import { ProcedureType } from '@/api/schema/procedures';
import { getProcedureQueryOptions } from '@/api/queries/procedures';
import { Route as SummaryRoute } from '@/routes/_operator/subjects/$subjectId/procedures/$procedureId/summary';
import { InstructionsDialog } from '@/components/instructionsDialog';

export const Route = createFileRoute(
  '/_operator/subjects/$subjectId/procedures/$procedureId/procedure/',
)({
  component: RouteComponent,
});

interface ProcedureStep {
  id: string;
  name: string;
  status: CueingStepStatus | LocalOnlyStepStatus;
  type: CueingStepTypeEnum;
}
interface MissingVasWarningParams {
  procedureState: ProcedureState;
  step: ProcedureStep;
}

const isVasResultMissingWarning = ({
  step,
  procedureState,
}: MissingVasWarningParams): boolean => {
  if (step.type !== CueingStepTypeEnum.VAS) return false;
  // key has the following format: i.e. c149cc96-30f0-4add-9881-cd7bdcb6bb47_sub_nicotine
  // so need to find by string includes
  const relatedKeys = Object.keys(procedureState.responses).filter(k =>
    k.includes(step.id),
  );
  if (relatedKeys.length === 0) return false;

  const relatedVasResponses = relatedKeys.map(
    k => procedureState.responses[k],
  );

  if (relatedVasResponses.length === 0) return false;
  return relatedVasResponses.some(r => !r.confirmed);
};

function RouteComponent() {
  const { t } = useTranslation();
  const params = Route.useParams();
  const { subjectId, procedureId } = params as {
    subjectId: string;
    procedureId: string;
  };
  const navigate = useNavigate();

  const queingBlockQuery = useSuspenseQuery(
    getProcedureQueingBlocksQueryOptions(procedureId),
  );
  const { data: procedure } = useSuspenseQuery(
    getProcedureQueryOptions(procedureId),
  );
  const updateCravingResultsMutation
    = useUpdateCravingResultsMutation(procedureId);
  const updateEmotionResultsMutation
    = useUpdateEmotionResultsMutation(procedureId);
  const { convertBlocksToSteps } = useProcedureSteps(subjectId, procedureId);
  const stopProcedureMutation = useStopProcedureMutation({ procedureId });
  const stopResumeProcedureMutation = useStopResumeProcedureMutation({
    procedureId,
  });
  const pauseProcedureMutation = usePauseProcedureMutation({ procedureId });
  const nextBlockMutation = useNextBlockMutation({ procedureId });
  const finishCueingProcedureMutation = useFinishCueingProcedure();
  const pauseResumeProcedureMutation = usePauseResumeProcedureMutation({
    procedureId,
  });
  const initializationRef = useRef(false);
  const initializeStepRef = useRef(false);
  const [ isFinishOpen, setIsFinishOpen ] = useState(false);

  const [ isRepeatVasModalOpen, setIsRepeatVasModalOpen ]
    = useState<boolean>(false);
  const commentMutation = useAddComment({ procedureId });
  const [ commentModalState, setCommentModalState ] = useState<Omit<
    CommentModalProps,
    'isOpen'
  > | null>(null);

  const onStepResponse = useCallback((stepId: string, response: unknown) => {
    logger(`Step ${stepId} response:`, response);
  }, []);

  const onConnectionChange = useCallback((connected: boolean) => {
    logger('Connection status:', connected ? 'Connected' : 'Disconnected');
  }, []);

  const onResponse = useCallback(
    (response: ProcedureStepResponse, index: number) => {
      logger('Procedure response:', response, 'at index', index);
      if (response.type === 'VAS') {
        if (response.substanceId) {
          updateCravingResultsMutation.mutate({
            isTimeouted: !response.confirmed,
            score: response.value,
            stepNumber: new CravingResultsCounter(
              procedureId,
            ).getSubstanceStepNumber(response.substanceId),
            substanceId: response.substanceId ?? '',
            substance: response.substance ?? '',
          });
        }
        else {
          console.error('VAS response missing substance ID');
          throw new Error('VAS response missing substance ID');
        }
      }

      if (response.type === 'EMOTION') {
        if (!response.emotion) {
          console.error('Emotion response missing emotion type');
          throw new Error('Emotion response missing emotion type');
        }

        updateEmotionResultsMutation.mutate({
          isTimeouted: !response.confirmed,
          score: response.value,
          stepNumber: new EmotionResultsCounter(
            procedureId,
          ).getEmotionStepNumber(response.emotion),
          emotionType: response.emotion,
        });
      }
    },
    [],
  );

  const {
    procedureState,
    currentStep,
    isConnected,
    isCompleted,
    isPaused,
    isStopped,
    connectionStatus,
    initializeProcedure,
    nextStep,
    goToStep,
    pauseProcedure,
    resumePausedProcedure,
    stopProcedure,
    resumeStoppedProcedure,
    clearStepResults,
  } = useProcedureManager({
    onStepResponse,
    onConnectionChange,
    onResponse,
    procedureId,
  });

  const updateCueingBlockMutation = useUpdateCueingBlockMutation();
  const blockNames = queingBlockQuery.data.items.map(block => block.name);

  const getCurrentBlock = useCallback(() => {
    const blockInProgress = queingBlockQuery.data.items.find(
      block => block.status === CueingBlockStatus.IN_PROGRESS,
    );
    if (blockInProgress) return blockInProgress;

    const completedBlockIndex = queingBlockQuery.data.items.findLastIndex(
      block => block.status === CueingBlockStatus.COMPLETED,
    );
    if (completedBlockIndex !== -1) {
      if (completedBlockIndex + 1 < queingBlockQuery.data.items.length) {
        return queingBlockQuery.data.items[completedBlockIndex + 1];
      }
      return queingBlockQuery.data.items[completedBlockIndex]; // TODO check: All blocks completed, return the last one
    }
    return queingBlockQuery.data.items[0]; // No blocks started yet, return the first step
  }, [ queingBlockQuery.data.items ]);

  const computedCurrentBlock = getCurrentBlock();

  // Calculate the correct starting step index based on backend state
  const calculateStartingStepIndex = useCallback(
    (allSteps: { id: string; originalStepId?: string }[]) => {
      // If procedure is completed, start from the last step
      if (isCompleted) {
        return allSteps.length - 1;
      }

      const currentBackendBlock = getCurrentBlock();

      // Find the first backend step that is not completed
      const firstIncompleteBackendStep = currentBackendBlock.cueingSteps.find(
        backendStep => backendStep.status !== CueingStepStatus.COMPLETED,
      );

      const allBlocksNotStarted = queingBlockQuery.data.items.every(
        block => block.status === CueingBlockStatus.NOT_STARTED,
      );

      if (!firstIncompleteBackendStep || allBlocksNotStarted) {
        return 0;
      }

      // Find the index of the first procedure step that corresponds to this backend step
      const stepIndex = allSteps.findIndex(
        step =>
          step.originalStepId === firstIncompleteBackendStep.id
          || step.id === firstIncompleteBackendStep.id,
      );

      return stepIndex >= 0 ? stepIndex : 0;
    },
    [ isCompleted, computedCurrentBlock ],
  );

  const startAssessment = useCallback(() => {
    const currentBlock = getCurrentBlock();
    updateCueingBlockMutation.mutate(
      {
        blockId: currentBlock.id,
        procedureId,
        status: CueingBlockStatus.IN_PROGRESS,
      },
      {
        onSettled: () => {
          nextStep();
        },
      },
    );
  }, [ getCurrentBlock, nextStep, procedureId ]);

  useEffect(() => {
    if (!currentStep) return;

    if (
      currentStep.type === ProcedureStepType.BLOCK_END_AUTO_CONTINUE
      && !nextBlockMutation.isPending
    ) {
      nextBlockMutation.mutate();
      nextStep();
    }

    if (
      currentStep.type === ProcedureStepType.PROCEDURE_END_AUTO_CONTINUE
      && !finishCueingProcedureMutation.isPending
    ) {
      finishCueingProcedureMutation.mutate(
        {
          procedureId,
        },
        {
          onSettled: () => {
            void navigate({
              to: SummaryRoute.to,
              params: { subjectId, procedureId },
            });
          },
        },
      );
    }

    if (
      currentStep.type === ProcedureStepType.PROCEDURE_AUTO_START
      && !updateCueingBlockMutation.isPending
    ) {
      startAssessment();
    }
  }, [ currentStep, nextStep, startAssessment ]);

  const activeBlock = useMemo(() => {
    if (!currentStep) return computedCurrentBlock;

    const currentOriginalStepId = currentStep.originalStepId ?? currentStep.id;

    const blockContainingStep = queingBlockQuery.data.items.find(block =>
      block.cueingSteps.some(step => step.id === currentOriginalStepId),
    );

    return blockContainingStep ?? computedCurrentBlock;
  }, [ currentStep, queingBlockQuery.data.items, computedCurrentBlock ]);

  const procedureSteps = useMemo((): ProcedureStep[] => {
    const baseSteps = activeBlock.cueingSteps.map(step => ({
      id: step.id,
      name: step.type.name,
      status: step.status,
      type: step.type.type,
    }));

    if (isCompleted) {
      return baseSteps.map(step => ({
        ...step,
        status: CueingStepStatus.COMPLETED,
      }));
    }

    if (!currentStep) {
      return baseSteps;
    }

    const currentOriginalStepId = currentStep.originalStepId ?? currentStep.id;
    const currentStepIndex = baseSteps.findIndex(
      s => s.id === currentOriginalStepId,
    );

    const getCurrentStepStatus = (): CueingStepStatus | LocalOnlyStepStatus => {
      if (
        currentStep.type === ProcedureStepType.BLOCK_END
        || currentStep.type === ProcedureStepType.PROCEDURE_END
      ) {
        return CueingStepStatus.COMPLETED;
      }
      if (isPaused || isStopped) {
        return CueingStepStatus.PAUSED;
      }
      if (currentStep.screen === ProcedureScreen.WAITING_FOR_OPERATOR) {
        return LocalOnlyStepStatus.WAITING_FOR_ACTION;
      }
      if (currentStep.screen === ProcedureScreen.FOCUS_CROSS) {
        return LocalOnlyStepStatus.FOCUS_CROSS;
      }
      return CueingStepStatus.IN_PROGRESS;
    };

    return baseSteps.map((step, stepIndex) => {
      if (step.id === currentOriginalStepId) {
        return { ...step, status: getCurrentStepStatus() };
      }

      if (currentStepIndex !== -1 && stepIndex < currentStepIndex) {
        return { ...step, status: CueingStepStatus.COMPLETED };
      }
      if (currentStepIndex !== -1 && stepIndex > currentStepIndex) {
        return { ...step, status: CueingStepStatus.NOT_STARTED };
      }

      return step;
    });
  }, [ activeBlock.cueingSteps, currentStep, isCompleted, isPaused, isStopped ]);

  // Handle procedure initialization - only run once
  useEffect(() => {
    if (initializationRef.current) {
      return; // Already initialized
    }

    if (procedureState.steps.length > 0) {
      initializationRef.current = true;
      return; // Already initialized from persistent state
    }

    const handleStartProcedure = async () => {
      try {
        const steps = await convertBlocksToSteps(queingBlockQuery.data.items);

        // Initialize procedure starting from the correct step
        initializeProcedure(steps);
        initializationRef.current = true;

        if (!isConnected) {
          openLinkInNewTab(`/patientApp/${procedureId}`, 'patient-app-window');
        }
      }
      catch (error) {
        console.error('Failed to initialize procedure:', error);
      }
    };

    void handleStartProcedure();
  }, [ procedureState ]);

  useEffect(() => {
    if (!initializeStepRef.current && procedureState.steps.length > 0) {
      const startingStepIndex = calculateStartingStepIndex(
        procedureState.steps,
      );

      if (!procedureState.isPaused && !procedureState.isStopped) {
        if (startingStepIndex >= procedureState.currentStepIndex) {
          goToStep(startingStepIndex);
        }
        else {
          // reinicialize step and reset timeouts
          goToStep(procedureState.currentStepIndex);
        }
      }

      initializeStepRef.current = true;
    }
  }, [
    computedCurrentBlock,
    procedureState.steps,
    procedureState.currentStepIndex,
  ]);

  const lastCompletedVAS = procedureSteps.findLast(
    step =>
      step.type === CueingStepTypeEnum.VAS
      && (step.status === CueingStepStatus.COMPLETED
        || step.status === LocalOnlyStepStatus.WAITING_FOR_ACTION),
  );

  const repeatVAS = (stepId: string) => {
    let stepIndex = procedureState.steps.findIndex(
      step => step.originalStepId === stepId,
    );

    if (stepIndex === -1) return;

    while (
      stepIndex < procedureState.steps.length
      && procedureState.steps[stepIndex].screen
      === ProcedureScreen.WAITING_FOR_OPERATOR
    ) {
      stepIndex++;
    }

    clearStepResults(stepId);
    goToStep(stepIndex);
    setIsRepeatVasModalOpen(true);
  };

  const onClickNext = useCallback(() => {
    nextStep();
  }, [ nextStep, getCurrentBlock ]);

  const openPatientApp = useCallback(() => {
    openLinkInNewTab(`/patientApp/${procedureId}`, 'patient-app-window');
  }, [ procedureId ]);

  const getStepStatus = (step: ProcedureStep) => {
    if (step.id === currentStep?.originalStepId && isStopped) {
      return CueingStepStatus.STOPPED;
    }

    if (step.id === currentStep?.originalStepId && isPaused) {
      return CueingStepStatus.PAUSED;
    }

    return step.status;
  };

  const actionButton = useMemo(() => {
    if (currentStep?.id === 'procedure_waiting_for_start') {
      return (
        <div className="flex flex-col p-space-lg items-end bg-fill-surface-weak rounded-2xl">
          <Button onClick={ startAssessment }>
            { t('buttons.startAssessment') }
          </Button>
        </div>
      );
    }

    if (currentStep?.type === ProcedureStepType.BLOCK_END) {
      return (
        <div className="flex flex-col p-space-lg items-end bg-fill-surface-weak rounded-2xl">
          <NextStep procedureId={ procedureId } onClick={ onClickNext } />
        </div>
      );
    }
  }, [ currentStep, procedureId ]);

  return (
    <div className="bg-fill-surface-strong flex-1 flex flex-col">
      <div className="mb-space-lg">
        <Header
          button={ (
            <Button
              onClick={ () => {
                setIsFinishOpen(true);
              } }
            >
              { t('finishCueing') }
            </Button>
          ) }
        />
      </div>
      <InstructionsDialog />
      { commentModalState && (
        <CommentModal
          isOpen={ Boolean(commentModalState) }
          { ...commentModalState }
        />
      ) }
      { isRepeatVasModalOpen && (
        <RepeatVasModal
          isOpen={ isRepeatVasModalOpen }
          onClose={ () => {
            setIsRepeatVasModalOpen(false);
          } }
        />
      ) }
      <div className="grid grid-cols-2 gap-space-lg flex-1">
        <div className="flex flex-col gap-space-lg">
          <DashboardCharts procedureId={ procedureId } />
          <PatientConnectionStatus
            status={ connectionStatus }
            onReopenPatientApp={ openPatientApp }
          />
        </div>
        <div className="flex flex-col gap-space-lg">
          <DynamicProgressSteps
            steps={ blockNames }
            currentStep={ isCompleted ? undefined : activeBlock.name }
          />
          <StepActions
            step={ activeBlock.name }
            showControlButtons={ procedure.type === ProcedureType.CLINICAL }
            onStopClick={ () => {
              stopProcedure();
              stopProcedureMutation.mutate();

              setCommentModalState({
                title: t('modals.commentModal.cueingStoppedTitle'),
                onClose: () => {
                  setCommentModalState(null);
                },
                onSave: (comment: string) => {
                  commentMutation.mutate({
                    commentText: comment,
                  });
                  setCommentModalState(null);
                },
              });
            } }
            onStopResumeClick={ (resumeFrom) => {
              const firstStepOfBlock = activeBlock.cueingSteps[0];

              stopResumeProcedureMutation.mutate(resumeFrom, {
                onSettled: () => {
                  void (async () => {
                    const steps = await convertBlocksToSteps(
                      queingBlockQuery.data.items,
                    );
                    resumeStoppedProcedure({
                      resumeFromBlockId: firstStepOfBlock.id,
                      steps,
                    });
                  })();
                },
              });
            } }
            onPauseClick={ () => {
              pauseProcedure();
              pauseProcedureMutation.mutate();

              setCommentModalState({
                title: t('modals.commentModal.cueingPausedTitle'),
                onClose: () => {
                  setCommentModalState(null);
                },
                onSave: (comment: string) => {
                  commentMutation.mutate({
                    commentText: comment,
                  });
                  setCommentModalState(null);
                },
              });
            } }
            onPauseResumeClick={ () => {
              resumePausedProcedure();
              pauseResumeProcedureMutation.mutate();
            } }
            isPaused={ isPaused }
            isStopped={ isStopped }
            stepPhases={ procedureSteps.map(step => ({
              name: step.name,
              status: getStepStatus(step),
              button:
                step.id === lastCompletedVAS?.id
                && step.status !== LocalOnlyStepStatus.WAITING_FOR_ACTION
                  ? (
                    <Button
                      variant="outline"
                      size="m"
                      onClick={ () => {
                        repeatVAS(step.id);
                      } }
                    >
                      { t('buttons.repeatVas') }
                    </Button>
                  )
                  : undefined,
              warning: isVasResultMissingWarning({ step, procedureState })
                ? {
                  title: t('warnings.vasResultsMissing.title'),
                  description: t('warnings.vasResultsMissing.description'),
                }
                : undefined,
            })) }
          />
          { actionButton }
        </div>
      </div>
      <FinishProcedureDialog
        open={ isFinishOpen }
        handleClose={ () => {
          setIsFinishOpen(false);
        } }
        procedureId={ procedureId }
        subjectId={ subjectId }
      />
    </div>
  );
}
